import { type Draft } from 'immer';

import { type ElementsState } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type HasDataProps,
  type PartialPropsOf,
  type TableGroupByProps,
} from '@blaze-runtime/engine-domain';
import {
  getFormatByDataType,
  setNumberColumnDefaultFormattingMutably,
  setTagsColumnDefaultColorPaletteMutably,
} from '@blaze-shared/elements-domain';
import {
  type CanvasElementDataProperties,
  type CanvasElementDirtyFieldPropsKeys,
  type CanvasElementInputBinding,
  type CanvasElementWithDirtyFieldProps,
  type DataBindingSourceInfoColumn,
  type DataSourceBindingDefinition,
  findDisplayColumnKey,
  findDisplayColumnOrGetSingle,
  findIdColumnKey,
  getActiveBinding,
  mergeColumns,
  type MergeColumnsConfig,
  type PropertiesConfigInputColumnModel,
  type PropertiesConfigInputListColumnModel,
  type PropertiesConfigInputListFiltersModel,
  type PropertiesConfigInputTableColumnModel,
  type PropertiesConfigSearchAndFiltersModel,
} from '@blaze-shared/elements-domain';
import { findColumn, findColumnKey, getColumnKey } from '@blaze-shared/schema-domain';
import {
  type BaseSchema,
  type BlazeTableSchema,
  type ColumnLike,
  type DatasetColumn,
  type ElementSchema,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { isDeepEqual, toResolvedColumnName } from '@blaze-shared/shared-util';
import { setValueByPath } from '@blaze-shared/workflow-domain';

import {
  hasDataProps,
  hasSearchAndFilters,
  hasTableGroupBy,
} from '../../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';
import { getDataSourceInputName } from '../../../canvas-elements/element-model/io';

const LIST_COLUMNS_MERGE_CONFIG: MergeColumnsConfig<
  PropertiesConfigInputListColumnModel,
  'id',
  'name'
> = {
  keyId: 'id',
  keyName: 'name',
  mapColumnFn: datasetColumnToListColumn,
  fieldsToUpdate: ['format', 'currency', 'precision', 'duration'], // TODO [MG] List columns migration.
};

const TABLE_COLUMNS_MERGE_CONFIG: MergeColumnsConfig<
  PropertiesConfigInputTableColumnModel,
  'id',
  'name'
> = {
  keyId: 'id',
  keyName: 'name',
  mapColumnFn: datasetColumnToTableColumn,
  initAppendedColumnFn: initAppendedTableColumnFn,
  fieldsToUpdate: ['currency', 'precision', 'duration'],
};

const NOTIFICATION_FIELD_UNREAD_GUESS = 'unread';

export function updateDataBindingMutably(
  data: CanvasElementDataProperties[],
  bindingConfig: DataSourceBindingDefinition[] | undefined,
  bindingKey: CanvasElementInputBinding,
  schema: BaseSchema | ElementSchema | WorkflowSchema,
  isNewDataSource: boolean,
): void {
  const binding = data.find((data) => data.key === bindingKey);
  if (!binding) {
    throw new Error(`Binding '${bindingKey}' missing`);
  }

  if (binding?.dataSource) {
    binding.dataSource.columns = [...schema.columns];
    binding.dataSource.displayColumn = findColumnKey(
      schema.columns,
      (schema as WorkflowSchema).displayColumn,
    );
    if (isNewDataSource || !binding.dataSource.name) {
      binding.dataSource.name = (schema as WorkflowSchema).tableName ?? schema.name;
    }
  }

  if (!bindingConfig) {
    throw new Error(`Missing '${bindingKey}' input bindings config`);
  }
  bindingConfig.forEach((config) => {
    // Keep binding if it's already bound to a column that exists
    if (findColumn(schema.columns, binding.bindings?.[config.property])) {
      return;
    }

    // Select default value & display column for display property when possible
    const idColumn =
      findIdColumnKey(schema) || findDisplayColumnKey(schema) || getColumnKey(schema.columns?.[0]);

    if (config.value && idColumn) {
      setBindingValueMutably(data, idColumn, config, bindingKey);
    } else if (config.display) {
      setBindingValueMutably(data, findDisplayColumnOrGetSingle(schema), config, bindingKey);
    }
  });

  // Only one column available - preselect the only available option
  if (bindingConfig.length && schema.columns?.length === 1) {
    const column = getColumnKey(schema.columns[0]);
    bindingConfig.forEach((b) => setBindingValueMutably(data, column, b, bindingKey));
  }
}

export function ensureApiCallDataSourceBindingMutably(
  draft: Draft<ElementsState>,
  elementId: CanvasElementId,
  workflowId: string,
  resultColumns: DatasetColumn[] | undefined,
): void {
  const element = draft.entities[elementId];
  if (!hasDataProps(element)) {
    console.error(`Element ${elementId} data missing`);
    return;
  }

  const bindingKey: CanvasElementInputBinding = getDataSourceInputName(element.type);
  const binding = element.props.data.find((data) => data.key === bindingKey);

  const newBinding: CanvasElementDataProperties = {
    key: bindingKey,
    dataSource: {
      ...binding?.dataSource,
      sourceType: 'context',
      id: workflowId,
      columns: resultColumns || [],
    },
    bindings: {},
    enabled: true,
    isCustomSelection: false,
  };

  if (!binding) {
    element.props.data.push(newBinding);
  } else if (
    !binding.enabled ||
    binding.dataSource?.sourceType !== 'context' ||
    binding.dataSource.id !== workflowId
  ) {
    binding.enabled = true;
    binding.dataSource = newBinding.dataSource;
  } else if (resultColumns) {
    binding.dataSource.columns = resultColumns;
  }
}

/**
 * Updates the element after the value binding or data source got updated
 * @param element the element which's binding got updated
 * @param isNewDataSource tells whether the data source got changed and the columns should be replaced; if false - the new columns will be merged with the existing setup
 * @param visibleFields table column whitelist (id-or-name); columns named in it are shown, the rest hidden - empty/omitted leaves every column visible
 */
export function updateElementAfterValueBindingsUpdateMutably(
  element: Draft<CanvasElement & HasDataProps>,
  isNewDataSource: boolean,
  filteredDatasetColumns: DatasetColumn[] | undefined = undefined,
  setNewColumnsVisible = false,
  setNewColumnsEditable = false,
  visibleFields?: string[],
): void {
  const valueColumns =
    element.props.data?.find((b) => b?.key === 'value')?.dataSource?.columns || [];
  if (isElementType(element, 'list')) {
    // Update and sanitize columns, fields and custom fields
    updateListColumnsMutably(element, valueColumns, filteredDatasetColumns, isNewDataSource);
  } else if (isElementType(element, 'kanbanBoard')) {
    updateKanbanBoardColumnsMutably(element, valueColumns, isNewDataSource);
  } else if (isElementType(element, 'table')) {
    // Update and sanitize columns
    updateTableColumnsMutably(
      element,
      valueColumns || [],
      isNewDataSource,
      setNewColumnsVisible,
      setNewColumnsEditable,
      visibleFields,
    );
  } else if (isElementType(element, 'ganttChart')) {
    updateGanttChartColumnsMutably(element, valueColumns, isNewDataSource);
  } else if (isElementType(element, 'notificationButton')) {
    updateNotificationButtonColumnsMutably(element, valueColumns, isNewDataSource);
  } else if (isElementType(element, 'calendar')) {
    updateCalendarColumnsMutably(element, valueColumns, isNewDataSource);
  } else if (isElementType(element, 'map')) {
    updateMapColumnsMutably(element, valueColumns, isNewDataSource);
  } else if (isElementType(element, 'chat')) {
    updateChatColumnsMutably(element, isNewDataSource);
  } else if (isElementType(element, 'availability')) {
    updateAvailabilityColumnsMutably(element, isNewDataSource);
  } else if (isElementType(element, 'checkbox') || isElementType(element, 'radio')) {
    updateCheckboxOrRadioColumnsMutably(element, isNewDataSource);
  }

  // Sanitize search and filters
  if (hasSearchAndFilters(element)) {
    element.props.content.searchAndFilters = sanitizeSearchAndFilters(
      element.props.content.searchAndFilters,
      filteredDatasetColumns,
      isNewDataSource,
    );
  }

  // Sanitize group by
  if (hasTableGroupBy(element)) {
    element.props.content.table.groupBy = sanitizeTableGroupBy(
      element.props.content.table.groupBy,
      valueColumns,
      isNewDataSource,
    );
  }
}

/**
 * Updates columns stored in list's model
 * @param isNewDataSource true if bindings changed and model should be replaced, false is used to refresh and merge changes
 */
function updateListColumnsMutably(
  element: CanvasElementFrom<'list'>,
  columns: DataBindingSourceInfoColumn[] | undefined,
  filteredDatasetColumns: DataBindingSourceInfoColumn[] | undefined,
  isNewDataSource: boolean,
): void {
  if (!columns) {
    setValueByPath(element.props, 'content.fields', {});
    setValueByPath(element.props, 'content.customFields', []);
  }

  const valueColumns = columns || [];

  // Update and sanitize columns
  const currentColumns = element.props.content?.columns;
  const mergedColumns = mergeColumns(
    LIST_COLUMNS_MERGE_CONFIG,
    valueColumns,
    currentColumns,
    isNewDataSource,
  );
  setValueByPath(element.props, 'content.columns', mergedColumns);

  const updatedColumns = element.props.content.columns;
  if (!updatedColumns) {
    throw new Error('Column data missing');
  }

  // Sanitize fields
  const fields = element.props.content?.fields;
  sanitizeListlikeFieldsMutably(fields, updatedColumns, [
    'image', // common
    'heading',
    'description', // list
    'name',
    'message',
    'isUnread',
    'timestamp', // notification
    'title',
    'price',
    'bulletList', // subscription plan
    'itemName',
    'itemDescription',
    'itemPrice',
    'originalPrice',
    'rating',
    'ratingVotes',
    'badge',
    'callout', // e-commerce
  ]);

  // Sanitize custom fields
  const customFields = element.props.content?.customFields;
  const updatedCustomFields = updateDirtyCustomFields(
    sanitizeListlikeCustomFields(customFields, updatedColumns),
    updatedColumns,
    element.props.featuresConfig?.fieldsLabelGen,
  );
  if (updatedCustomFields) {
    setValueByPath(element.props, 'content.customFields', updatedCustomFields);
  }

  // Apply defaults if new data source
  if (isNewDataSource) {
    if (!element.props.content.fields) {
      element.props.content.fields = {};
    }

    const fields = element.props.content.fields;

    if (!fields.image) {
      const column =
        updatedColumns.find((column) => column.format === 'image') ||
        updatedColumns.find((column) => column.name.toLowerCase().includes('image')) ||
        updatedColumns.find((column) => column.name.toLowerCase().includes('picture'));
      fields.image = getColumnKey(column);
    }
    if (!fields.heading) {
      fields.heading = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('name')),
      );
    }
    if (!fields.description) {
      fields.description = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase() === 'description') ||
          updatedColumns.find((column) => column.name.toLowerCase() === 'count'),
      );
    }
    if (!fields.name) {
      fields.name = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('name')),
      );
    }
    if (!fields.message) {
      fields.message = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('message')),
      );
    }
    if (!fields.isUnread) {
      fields.isUnread = getColumnKey(
        (filteredDatasetColumns ?? []).find(
          (column) =>
            column.dataType === 'Boolean' &&
            column.name.toLowerCase().includes(NOTIFICATION_FIELD_UNREAD_GUESS),
        ),
      );
    }
    if (!fields.timestamp) {
      const column =
        updatedColumns.find((column) => column.format === 'date' || column.format === 'datetime') &&
        updatedColumns.find((column) => column.name.toLowerCase().includes('timestamp'));
      fields.timestamp = getColumnKey(column);
    }
    if (!fields.title) {
      fields.title = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('title')),
      );
    }
    if (!fields.price) {
      fields.price = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('price')),
      );
    }
    if (!fields.bulletList) {
      fields.bulletList = getColumnKey(updatedColumns.find((column) => column.format === 'tag'));
    }
    if (!fields.itemName) {
      fields.itemName = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('name')),
      );
    }
    if (!fields.itemDescription) {
      fields.itemDescription = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('description')),
      );
    }
    if (!fields.itemPrice) {
      fields.itemPrice = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('price')),
      );
    }
    if (!fields.originalPrice) {
      fields.originalPrice = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('original')),
      );
    }
    if (!fields.rating) {
      fields.rating = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('rating')),
      );
    }
    if (!fields.ratingVotes) {
      fields.ratingVotes = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('votes')),
      );
    }
    if (!fields.badge) {
      fields.badge = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('badge')),
      );
    }
    if (!fields.callout) {
      fields.callout = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('callout')),
      );
    }
  }
}

// TODO: [MG] Remove duplicates
function updateKanbanBoardColumnsMutably(
  element: CanvasElementFrom<'kanbanBoard'>,
  columns: DataBindingSourceInfoColumn[] | undefined,
  isNewDataSource: boolean,
): void {
  if (!columns) {
    setValueByPath(element.props, 'content.fields', {});
    setValueByPath(element.props, 'content.customFields', []);
  }

  const valueColumns = columns || [];

  // Update and sanitize columns
  const currentColumns = element.props.content?.columns;
  const mergedColumns = mergeColumns(
    LIST_COLUMNS_MERGE_CONFIG,
    valueColumns,
    currentColumns,
    isNewDataSource,
  );
  setValueByPath(element.props, 'content.columns', mergedColumns);

  const updatedColumns = element.props.content.columns;
  if (!updatedColumns) {
    throw new Error('Column data missing');
  }

  // Sanitize fields
  const fields = element.props.content?.fields;
  sanitizeListlikeFieldsMutably(fields, updatedColumns, [
    'columnTitle',
    'cardTitle',
    'cardDescription',
  ]);

  // Sanitize custom fields
  const customFields = element.props.content?.customFields;
  const updatedCustomFields = updateDirtyCustomFields(
    sanitizeListlikeCustomFields(customFields, updatedColumns),
    updatedColumns,
    element.props.featuresConfig?.fieldsLabelGen,
  );
  if (updatedCustomFields) {
    setValueByPath(element.props, 'content.customFields', updatedCustomFields);
  }
}

function datasetColumnToListColumn(col: DatasetColumn): PropertiesConfigInputListColumnModel {
  const column: PropertiesConfigInputListColumnModel = {
    name: col.name,
    id: col.id,
    format: getFormatByDataType(col.dataType, col.format?.formatType),
  };
  setNumberColumnDefaultFormattingMutably(column, col);
  setTagsColumnDefaultColorPaletteMutably(column, col);
  return column;
}

/**
 * Updates columns stored in ganttCharts's model
 * @param isNewDataSource true if bindings changed and model should be replaced, false is used to refresh and merge changes
 */
function updateGanttChartColumnsMutably(
  element: CanvasElementFrom<'ganttChart'>,
  columns: DataBindingSourceInfoColumn[] | undefined,
  isNewDataSource: boolean,
): void {
  if (!columns) {
    setValueByPath(element.props, 'content.fields', {});
    setValueByPath(element.props, 'content.customFields', []);
  }

  const valueColumns = columns || [];

  // Update and sanitize columns
  const currentColumns = element.props.content?.columns;
  const mergedColumns = mergeColumns(
    LIST_COLUMNS_MERGE_CONFIG,
    valueColumns,
    currentColumns,
    isNewDataSource,
  );
  setValueByPath(element.props, 'content.columns', mergedColumns);

  const updatedColumns = element.props.content.columns;
  if (!updatedColumns) {
    throw new Error('Column data missing');
  }

  // Sanitize fields
  const fields = element.props.content?.fields;
  sanitizeListlikeFieldsMutably(fields, updatedColumns, ['task', 'startDate', 'endDate']);

  // Sanitize custom fields
  const customFields = element.props.content?.customFields;
  const updatedCustomFields = sanitizeListlikeCustomFields(customFields, updatedColumns);
  if (updatedCustomFields) {
    setValueByPath(element.props, 'content.customFields', updatedCustomFields);
  }

  // Apply defaults if new data source
  if (isNewDataSource) {
    if (!element.props.content.fields) {
      element.props.content.fields = {};
    }

    const fields = element.props.content.fields;

    if (!fields.task) {
      fields.task = getColumnKey(
        updatedColumns.find((column) => column.name.toLowerCase().includes('task')) ||
          updatedColumns.find((column) => column.name.toLowerCase().includes('name')),
      );
    }
    if (!fields.task) {
      fields.task = getColumnKey(updatedColumns.find((column) => column.format === 'text'));
    }
    let dateColumns = updatedColumns.filter(
      (column) => column.format === 'date' || column.format === 'datetime',
    );
    if (!fields.startDate) {
      const startColumn = dateColumns.find((column) => column.name.toLowerCase().includes('start'));
      dateColumns = dateColumns.filter((column) => column !== startColumn);
      fields.startDate = getColumnKey(startColumn);
    }
    if (!fields.endDate) {
      const endColumn = dateColumns.find((column) => column.name.toLowerCase().includes('end'));
      dateColumns = dateColumns.filter((column) => column !== endColumn);
      fields.endDate = getColumnKey(endColumn);
    }
    if (!fields.startDate) {
      fields.startDate = getColumnKey(dateColumns[0]);
    }
    if (!fields.endDate) {
      fields.endDate = getColumnKey(dateColumns[1]);
    }
  }
}

function updateMapColumnsMutably(
  element: CanvasElementFrom<'map'>,
  columns: DataBindingSourceInfoColumn[] | undefined,
  isNewDataSource: boolean,
): void {
  if (!columns) {
    setValueByPath(element.props, 'content.fields', {});
    setValueByPath(element.props, 'content.customFields', []);
    return;
  }

  if (!element.props?.content?.input) {
    setValueByPath(element.props, 'content.input', {});
  }

  const fields = element.props.content.input;

  if (!fields) {
    return;
  }

  sanitizeListlikeFieldsMutably(fields, columns, ['coordinates']);

  if (!isNewDataSource) {
    return;
  }

  if (!fields.coordinates) {
    fields.coordinates = getColumnKey(
      columns.find(
        (column) =>
          column.name.toLocaleLowerCase().includes('coord') ||
          column.name.toLocaleLowerCase().includes('geo') ||
          column.name.toLocaleLowerCase().includes('point'),
      ),
    );
  }

  const { markers } = element.props.content;

  if (markers && !markers.title) {
    markers.title = getColumnKey(
      columns.find(
        (column) =>
          column.name.toLocaleLowerCase().includes('title') ||
          column.name.toLocaleLowerCase().includes('name'),
      ),
    );
  }

  if (markers && !markers?.image) {
    markers.image = getColumnKey(
      columns.find(
        (column) =>
          column.columnType === 'Image' ||
          column.name.toLocaleLowerCase().includes('image') ||
          column.name.toLocaleLowerCase().includes('picture'),
      ),
    );
  }

  const { card } = element.props.content;

  if (card && !card.title) {
    card.title = getColumnKey(
      columns.find(
        (column) =>
          column.name.toLocaleLowerCase().includes('title') ||
          column.name.toLocaleLowerCase().includes('name'),
      ),
    );
  }

  if (card && !card?.image) {
    card.image = getColumnKey(
      columns.find(
        (column) =>
          column.columnType === 'Image' ||
          column.name.toLocaleLowerCase().includes('image') ||
          column.name.toLocaleLowerCase().includes('picture'),
      ),
    );
  }

  const valueColumns = columns || [];

  // Update and sanitize columns
  const currentColumns = element.props.content?.columns;
  const mergedColumns = mergeColumns(
    LIST_COLUMNS_MERGE_CONFIG,
    valueColumns,
    currentColumns,
    isNewDataSource,
  );
  setValueByPath(element.props, 'content.columns', mergedColumns);

  const updatedColumns = element.props.content.columns;
  if (!updatedColumns) {
    throw new Error('Column data missing');
  }
}

/**
 * Updates columns stored in notificationButton's model
 * @param isNewDataSource true if bindings changed and model should be replaced, false is used to refresh and merge changes
 */
function updateNotificationButtonColumnsMutably(
  element: CanvasElementFrom<'notificationButton'>,
  columns: DataBindingSourceInfoColumn[] | undefined,
  isNewDataSource: boolean,
): void {
  if (!columns) {
    setValueByPath(element.props, 'content.fields', {});
    setValueByPath(element.props, 'content.customFields', []);
  }

  const valueColumns = columns || [];

  // Update and sanitize columns
  const currentColumns = element.props.content?.columns;
  const mergedColumns = mergeColumns(
    LIST_COLUMNS_MERGE_CONFIG,
    valueColumns,
    currentColumns,
    isNewDataSource,
  );
  setValueByPath(element.props, 'content.columns', mergedColumns);

  const updatedColumns = element.props.content.columns;
  if (!updatedColumns) {
    throw new Error('Column data missing');
  }

  // Sanitize fields
  const fields = element.props.content?.fields;
  sanitizeListlikeFieldsMutably(fields, updatedColumns, ['isUnread']);

  // Apply defaults if new data source
  if (isNewDataSource) {
    if (!element.props.content.fields) {
      element.props.content.fields = {};
    }

    const fields = element.props.content.fields;

    if (!fields.isUnread) {
      fields.isUnread = getColumnKey(
        updatedColumns.find(
          (column) =>
            column.format === 'checkbox' &&
            column.name.toLowerCase().includes(NOTIFICATION_FIELD_UNREAD_GUESS),
        ),
      );
    }
  }
}

/**
 * Updates columns stored in table's model
 * @param isNewDataSource true if bindings changed and model should be replaced, false is used to refresh and merge changes
 * @param visibleFields whitelist (id-or-name) applied to the merged columns; empty/omitted leaves every column visible
 */
function updateTableColumnsMutably(
  element: CanvasElementFrom<'table'>,
  columns: DataBindingSourceInfoColumn[] | undefined,
  isNewDataSource = true,
  setNewColumnsVisible = false,
  setNewColumnsEditable = false,
  visibleFields?: string[],
): void {
  if (!columns) {
    return;
  }
  const currentColumns = element.props.content?.columns;
  const mergedColumns = mergeColumns(
    TABLE_COLUMNS_MERGE_CONFIG,
    columns,
    currentColumns,
    isNewDataSource,
    { setNewColumnsVisible, labelField: 'label', setNewColumnsEditable },
  );
  const columnsWithVisibility = visibleFields?.length
    ? applyVisibleFieldsWhitelist(mergedColumns, visibleFields)
    : mergedColumns;
  setValueByPath(element.props, 'content.columns', columnsWithVisibility);

  // Check if columns used for default sorting exist - if not remove them
  const propsSort = element.props.content.sort;
  const propsSorts = Array.isArray(propsSort) ? propsSort : propsSort ? [propsSort] : [];
  const propsSortsFixed = propsSorts
    .map((s) => ({
      field: getColumnKey(findColumn(columnsWithVisibility, s.field)),
      direction: s.direction,
    }))
    .filter((s) => !!s.field);
  if (!isDeepEqual(propsSorts, propsSortsFixed)) {
    setValueByPath(element.props, 'content.sort', propsSortsFixed);
  }
}

function applyVisibleFieldsWhitelist(
  columns: PropertiesConfigInputTableColumnModel[],
  visibleFields: string[],
): PropertiesConfigInputTableColumnModel[] {
  return columns.map((column) => ({
    ...column,
    visible: visibleFields.some((field) => !!findColumn([column], field)),
  }));
}

function updateCalendarColumnsMutably(
  element: CanvasElementFrom<'calendar'>,
  columns: DataBindingSourceInfoColumn[] | undefined,
  isNewDataSource: boolean,
): void {
  if (!columns) {
    setValueByPath(element.props, 'content.input', {});
    return;
  }

  if (!element.props?.content?.input) {
    setValueByPath(element.props, 'content.input', {});
  }

  const fields = element.props.content.input;

  if (!fields) {
    return;
  }

  sanitizeListlikeFieldsMutably(fields, columns, [
    'titleColumn',
    'startColumn',
    'endColumn',
    'calendarColumn',
    'timeZoneColumn',
  ]);

  if (!isNewDataSource) {
    return;
  }

  // Apply defaults if new data source
  if (!fields.titleColumn) {
    fields.titleColumn = getColumnKey(
      columns.find(
        (column) =>
          column.name.toLocaleLowerCase().includes('title') ||
          column.name.toLocaleLowerCase().includes('name') ||
          column.name.toLocaleLowerCase().includes('appointment'),
      ),
    );
  }

  const dateColumns = columns.filter((column) => column.columnType === 'DateTime');

  if (!fields.startColumn) {
    fields.startColumn = getColumnKey(
      dateColumns.find(
        (column) =>
          column.name.toLocaleLowerCase().includes('start') ||
          column.name.toLocaleLowerCase().includes('from'),
      ),
    );
  }

  if (!fields.endColumn) {
    fields.endColumn = getColumnKey(
      dateColumns.find(
        (column) =>
          column.name.toLocaleLowerCase().includes('end') ||
          column.name.toLocaleLowerCase().includes('to'),
      ),
    );
  }

  if (!fields.calendarColumn) {
    fields.calendarColumn = getColumnKey(columns.find((column) => column.columnType === 'Link'));
  }

  if (!fields.timeZoneColumn) {
    fields.timeZoneColumn = getColumnKey(
      columns.find(
        (column) =>
          column.columnType === 'TimeZone' ||
          column.name.toLocaleLowerCase().includes('timezone') ||
          column.name.toLocaleLowerCase().includes('time zone'),
      ),
    );
  }
}

function updateChatColumnsMutably(
  element: CanvasElementFrom<'chat'>,
  isNewDataSource: boolean,
): void {
  const messagesValueColumns =
    getActiveBinding(element.props.data, 'messages')?.dataSource?.columns || [];
  const conversationsValueColumns =
    getActiveBinding(element.props.data, 'conversations')?.dataSource?.columns || [];
  const conversationsInfoValueColumns =
    getActiveBinding(element.props.data, 'conversationsInfo')?.dataSource?.columns || [];

  const partialProps = element.props as PartialPropsOf<'chat'>;

  if (!partialProps.content) {
    partialProps.content = {};
  }

  if (!partialProps.content.input) {
    partialProps.content.input = {
      messages: {},
      conversations: {},
      conversationsInfo: {},
    };
  }

  if (!partialProps.content.actions) {
    partialProps.content.actions = {};
  }

  if (!partialProps.content.input.messages) {
    partialProps.content.input.messages = {};
  }

  if (!partialProps.content.input.conversations) {
    partialProps.content.input.conversations = {};
  }

  if (!partialProps.content.input.conversationsInfo) {
    partialProps.content.input.conversationsInfo = {};
  }

  const messagesColumns = partialProps.content.input.messages;
  const conversationsColumns = partialProps.content.input.conversations;
  const conversationsInfoColumns = partialProps.content.input.conversationsInfo;

  if (!isNewDataSource) {
    return;
  }

  if (messagesValueColumns.length) {
    if (!messagesColumns?.id) {
      messagesColumns.id = getColumnKey(
        messagesValueColumns.find(
          (column) => !column.isInternal && column.name.toLocaleLowerCase().includes('id'),
        ),
      );
    }

    if (!messagesColumns.sender) {
      messagesColumns.sender = getColumnKey(
        messagesValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('sender') ||
            column.name.toLocaleLowerCase().includes('author') ||
            column.name.toLocaleLowerCase().includes('user'),
        ),
      );
    }

    if (!messagesColumns.messageBody) {
      messagesColumns.messageBody = getColumnKey(
        messagesValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('body') ||
            column.name.toLocaleLowerCase().includes('content') ||
            column.name.toLocaleLowerCase().includes('message'),
        ),
      );
    }

    if (!messagesColumns.createdTime) {
      messagesColumns.createdTime = getColumnKey(
        messagesValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('create') ||
            column.name.toLocaleLowerCase().includes('add'),
        ),
      );
    }

    if (!messagesColumns.updatedTime) {
      messagesColumns.updatedTime = getColumnKey(
        messagesValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('update') ||
            column.name.toLocaleLowerCase().includes('edit'),
        ),
      );
    }

    if (!messagesColumns.modifiedTime) {
      messagesColumns.modifiedTime = getColumnKey(
        messagesValueColumns.find(
          (column) =>
            getColumnKey(column) !== messagesColumns.updatedTime &&
            (column.name.toLocaleLowerCase().includes('modified') ||
              column.name.toLocaleLowerCase().includes('modify') ||
              column.name.toLocaleLowerCase().includes('update') ||
              column.name.toLocaleLowerCase().includes('edit')),
        ),
      );
    }

    if (!messagesColumns.conversation) {
      messagesColumns.conversation = getColumnKey(
        messagesValueColumns.find((column) => column.name.toLocaleLowerCase().includes('conv')),
      );
    }

    if (!messagesColumns.attachments) {
      messagesColumns.attachments = getColumnKey(
        messagesValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('attach') ||
            column.name.toLocaleLowerCase().includes('file') ||
            column.name.toLocaleLowerCase().includes('image'),
        ),
      );
    }
  }

  if (conversationsValueColumns.length) {
    if (!conversationsColumns?.id) {
      conversationsColumns.id = getColumnKey(
        conversationsValueColumns.find(
          (column) => !column.isInternal && column.name.toLocaleLowerCase().includes('id'),
        ),
      );
    }

    if (!conversationsColumns?.name) {
      conversationsColumns.name = getColumnKey(
        conversationsValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('name') ||
            column.name.toLocaleLowerCase().includes('conv'),
        ),
      );
    }

    if (!conversationsColumns?.members) {
      conversationsColumns.members = getColumnKey(
        conversationsValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('member') ||
            column.name.toLocaleLowerCase().includes('user'),
        ),
      );
    }

    if (!conversationsColumns?.createdBy) {
      conversationsColumns.createdBy = getColumnKey(
        conversationsValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('created') ||
            column.name.toLocaleLowerCase().includes('author'),
        ),
      );
    }
  }

  if (conversationsInfoValueColumns.length) {
    if (!conversationsInfoColumns.conversation) {
      conversationsInfoColumns.conversation = getColumnKey(
        conversationsInfoValueColumns.find((column) =>
          column.name.toLocaleLowerCase().includes('conv'),
        ),
      );
    }

    if (!conversationsInfoColumns.membersName) {
      conversationsInfoColumns.membersName = getColumnKey(
        conversationsInfoValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('name') ||
            column.name.toLocaleLowerCase() === 'member',
        ),
      );
    }

    if (!conversationsInfoColumns.membersEmail) {
      conversationsInfoColumns.membersEmail = getColumnKey(
        conversationsInfoValueColumns.find((column) =>
          column.name.toLocaleLowerCase().includes('email'),
        ),
      );
    }

    if (!conversationsInfoColumns.membersImage) {
      conversationsInfoColumns.membersImage = getColumnKey(
        conversationsInfoValueColumns.find((column) =>
          column.name.toLocaleLowerCase().includes('image'),
        ),
      );
    }

    if (!conversationsInfoColumns.lastMessageId) {
      conversationsInfoColumns.lastMessageId = getColumnKey(
        conversationsInfoValueColumns.find(
          (column) =>
            column.name.toLocaleLowerCase().includes('last') ||
            column.name.toLocaleLowerCase().includes('message'),
        ),
      );
    }
  }
}

function updateAvailabilityColumnsMutably(
  element: CanvasElementFrom<'availability'>,
  isNewDataSource: boolean,
): void {
  if (!isNewDataSource) {
    return;
  }
  const columns = getActiveBinding(element.props.data, 'value')?.dataSource?.columns || [];
  if (!columns.length) {
    return;
  }

  const partialProps = element.props as PartialPropsOf<'availability'>;
  if (!partialProps.content) {
    partialProps.content = {};
  }
  if (!partialProps.content.input) {
    partialProps.content.input = {};
  }

  const availabilityColumns = partialProps.content.input;

  if (!availabilityColumns.dayOfWeekColumn) {
    availabilityColumns.dayOfWeekColumn = getColumnKey(
      columns.find((column) => {
        const cn = column.name.toLocaleLowerCase();
        return (
          !column.isInternal &&
          column.columnType === 'Number' &&
          (cn.includes('day') || cn.includes('week'))
        );
      }),
    );
  }

  if (!availabilityColumns.startDateColumn) {
    availabilityColumns.startDateColumn = getColumnKey(
      columns.find((column) => {
        const cn = column.name.toLocaleLowerCase();
        return (
          !column.isInternal &&
          column.columnType === 'Date' &&
          (cn.includes('start') || cn.includes('since'))
        );
      }),
    );
  }

  if (!availabilityColumns.endDateColumn) {
    availabilityColumns.endDateColumn = getColumnKey(
      columns.find((column) => {
        const cn = column.name.toLocaleLowerCase();
        return (
          !column.isInternal &&
          column.columnType === 'Date' &&
          (cn.includes('end') || cn.includes('until'))
        );
      }),
    );
  }

  if (!availabilityColumns.startTimeColumn) {
    availabilityColumns.startTimeColumn = getColumnKey(
      columns.find((column) => {
        const cn = column.name.toLocaleLowerCase();
        return (
          !column.isInternal &&
          column.columnType === 'Time' &&
          (cn.includes('start') || cn.includes('since'))
        );
      }),
    );
  }

  if (!availabilityColumns.endTimeColumn) {
    availabilityColumns.endTimeColumn = getColumnKey(
      columns.find((column) => {
        const cn = column.name.toLocaleLowerCase();
        return (
          !column.isInternal &&
          column.columnType === 'Time' &&
          (cn.includes('end') || cn.includes('until'))
        );
      }),
    );
  }
}

function updateCheckboxOrRadioColumnsMutably(
  element: CanvasElementFrom<'checkbox' | 'radio'>,
  isNewDataSource: boolean,
): void {
  if (!isNewDataSource) {
    return;
  }
  const activeBinding = getActiveBinding(element.props.data, 'optionsList');
  if (!activeBinding?.dataSource) {
    return;
  }
  const dataSource = activeBinding.dataSource;

  const searchForSimilarColumn = (columns: string[], colType?: string): string | undefined => {
    return dataSource?.columns.find((col) =>
      columns.find(
        (c) =>
          !col.isInternal &&
          ((colType && col.columnType === colType) || !colType) &&
          col.name.toLocaleLowerCase().includes(c),
      ),
    )?.id;
  };

  const bindings = Object.fromEntries(
    [
      ['value', dataSource?.columns.find((col) => col.isId)?.id],
      [
        'label',
        searchForSimilarColumn(['label', 'name', 'title', 'text']) ||
          dataSource.columns.find((col) => !col.isInternal)?.id,
      ],
      ['imageSrc', searchForSimilarColumn(['image', 'picture'], 'Image')],
      ['subheading', searchForSimilarColumn(['subtitle', 'subheading'])],
      ['description', searchForSimilarColumn(['description', 'details', 'info'])],
    ].filter(([, v]) => v !== undefined),
  );

  activeBinding.bindings = bindings;
}

function initAppendedTableColumnFn(
  column: PropertiesConfigInputTableColumnModel,
  visible = false,
  editable = false,
): PropertiesConfigInputTableColumnModel {
  return { ...column, visible, editable };
}

function datasetColumnToTableColumn(
  col: DatasetColumn,
  _accountsTableName: string | undefined,
  fieldsLabelGen: number | undefined,
): PropertiesConfigInputTableColumnModel {
  const column: PropertiesConfigInputTableColumnModel = {
    name: col.name,
    id: col.id,
    label: toResolvedColumnName(col.name, fieldsLabelGen),
    format: getFormatByDataType(col.dataType, col.format?.formatType),
    visible: true,
    align: 'left',
  };

  setNumberColumnDefaultFormattingMutably(column, col);
  setTagsColumnDefaultColorPaletteMutably(column, col);
  return column;
}

function setBindingValueMutably(
  draftData: CanvasElementDataProperties[],
  value: string,
  binding: DataSourceBindingDefinition,
  key: string,
): void {
  const index = draftData.findIndex((b) => b.key === key);
  if (index === undefined || index < 0) {
    return;
  }

  draftData[index].bindings = {
    ...draftData[index]?.bindings,
    [binding.property]: value,
  };
}

function sanitizeListlikeFieldsMutably<T extends object>(
  fields: T | undefined,
  updatedColumns: ColumnLike[],
  availableFields: (keyof T)[],
): void {
  if (!fields) {
    return;
  }

  for (const key of availableFields) {
    // If field is assigned to a column
    const field = fields[key];
    if (field && typeof field === 'string') {
      const matchedColumn = findColumn(updatedColumns, field);
      if (matchedColumn) {
        // Update the field selection with matched column's latest key (name -> column id)
        fields[key] = getColumnKey(matchedColumn) as T[keyof T];
      } else {
        // Remove the field selection if field's assigned column couldn't be found
        delete fields[key];
      }
    }
  }
}

function sanitizeListlikeCustomFields<T extends { column?: string }>(
  customFields: T[] | undefined,
  updatedColumns: PropertiesConfigInputColumnModel[],
): T[] | undefined {
  if (!customFields) {
    return;
  }

  return customFields.reduce<T[]>((acc, customField) => {
    const matchedColumn = findColumn(updatedColumns, customField.column);
    if (matchedColumn) {
      // Make sure the latest known column key is used (name -> column id)
      const columnKey = getColumnKey(matchedColumn);
      const updatedField =
        customField.column === columnKey ? customField : { ...customField, column: columnKey }; // Update column if key is different
      // Matched columns should be kept after refreshing
      acc.push(updatedField);
    }
    return acc;
  }, []);
}

function sanitizeSearchAndFilters(
  model: PropertiesConfigSearchAndFiltersModel,
  filteredDatasetColumns: DatasetColumn[] | undefined,
  isNewDataSource: boolean,
): PropertiesConfigSearchAndFiltersModel {
  const updatedModel = { ...model };

  // Clear filters that are empty or not present in new data source
  if (model.filters && filteredDatasetColumns) {
    updatedModel.filters = model.filters.reduce<PropertiesConfigInputListFiltersModel[]>(
      (acc, filter) => {
        if (!filter.filterField) {
          return acc;
        }

        const matchedColumn = findColumn(filteredDatasetColumns, filter.filterField);
        if (matchedColumn) {
          // Make sure the latest known column key is used (name -> column id)
          const columnKey = getColumnKey(matchedColumn);
          const updatedFilter =
            filter.filterField === columnKey ? filter : { ...filter, filterField: columnKey }; // Update field if key is different

          // Matched columns should be kept after refreshing
          acc.push(updatedFilter);
        }
        return acc;
      },
      [],
    );
  } else {
    updatedModel.filters = [];
  }

  // Clear selected search columns on bindings change
  if (isNewDataSource) {
    delete updatedModel.useUserSelectedSearchColumns;
    delete updatedModel.searchColumns;
  }
  // For existing search settings make sure that search included columns exists
  else if (model.searchColumns && filteredDatasetColumns) {
    updatedModel.searchColumns = model.searchColumns.filter((columnIdOrName) =>
      findColumn(filteredDatasetColumns, columnIdOrName),
    );
  }

  return updatedModel;
}

function sanitizeTableGroupBy(
  model: TableGroupByProps['content']['table']['groupBy'],
  dataSourceColumns: DataBindingSourceInfoColumn[],
  isNewDataSource: boolean,
): TableGroupByProps['content']['table']['groupBy'] {
  if (isNewDataSource) {
    return [];
  }

  if (!dataSourceColumns.length) {
    return model;
  }

  return [...model].map((col) => {
    return {
      ...col,
      field: col.field ? getColumnKey(findColumn(dataSourceColumns, col.field)) : col.field,
    };
  });
}

function updateDirtyCustomFields<T extends { column?: string }>(
  customFields: T[] | undefined,
  updatedColumns: PropertiesConfigInputColumnModel[],
  fieldsLabelGen: number | undefined,
): T[] | undefined {
  const fieldsToUpdate = ['displayFormat', 'currency', 'precision', 'duration'];
  return customFields?.map((customField) => {
    const colToMerge = updatedColumns.find((c) => c.id === customField.column);
    if (!colToMerge) {
      return customField;
    }
    const field = customField as CanvasElementWithDirtyFieldProps & { label?: string };

    if (
      field.dirty?.label &&
      field.label &&
      field.label === toResolvedColumnName(colToMerge.name, fieldsLabelGen)
    ) {
      delete field.dirty.label;
    }

    fieldsToUpdate?.forEach((fieldName) => {
      const dirtyProp = (
        fieldName !== 'displayFormat' ? fieldName : 'format'
      ) as CanvasElementDirtyFieldPropsKeys;
      if (fieldName in customField) {
        (customField as any)[fieldName] = (colToMerge as any)[dirtyProp];
      }
      if (field && field.dirty?.[dirtyProp]) {
        delete field.dirty[dirtyProp];
      }
    });

    const fieldWithDirtyKeys = Object.keys(field.dirty ?? {});
    if (field.dirty && !fieldWithDirtyKeys.length) {
      delete field.dirty;
    }
    return customField;
  });
}

export function initializeAvatarFieldsMutably(
  element: Draft<CanvasElementFrom<'avatar'>>,
  schema: BlazeTableSchema,
): void {
  const fields = { ...element.props.content?.fields };
  if (!fields.name) {
    fields.name = (
      schema.columns.find(({ name }) => name.toLocaleLowerCase() === 'name') ??
      schema.columns.find(({ name }) => name.toLocaleLowerCase().includes('name'))
    )?.name;
  }
  if (!fields.image) {
    fields.image = schema.columns.find(({ dataType }) => dataType === 'Image')?.name;
  }
  element.props.content = {
    ...element.props.content,
    fields,
  };
}
